import { useState } from 'react';
import { Icon } from '../ui/Icon';
import { logoUrl } from '../api/client';
import { TRACKER_IDS, TRACKER_NAMES, type CasinoRow, type Payload, type WindowKey } from '../api/types';
import { gapStep, pct, usd } from '../lib/format';
import { Rail, finite } from '../ui/charts';
import { Logo } from '../ui/Logo';
import { openRowLink } from '../ui/rowLink';

type Only = 'all' | 'multi' | 'gap';

export function Casinos({ data, win, search }: { data: Payload; win: WindowKey; search: URLSearchParams }) {
  const [query, setQuery] = useState(search.get('q') ?? '');
  const initial = search.get('only');
  const [only, setOnly] = useState<Only>(initial === 'multi' || initial === 'gap' ? initial : 'all');

  const a = data.analysis.volume[win];
  if (!a) return <div className="tile"><p className="empty">Nothing published for this window.</p></div>;

  const q = query.trim().toLowerCase();
  let rows = a.rows.filter((r) => !q || r.name.toLowerCase().includes(q));
  if (only === 'multi') rows = rows.filter((r) => r.consensusBy.length >= 2);
  if (only === 'gap') {
    rows = rows.filter((r) => r.consensusBy.length > 1 && finite(r.spreadPct))
      .sort((x, y) => (y.spreadPct ?? 0) - (x.spreadPct ?? 0));
  }

  return (
    <div className="page">
      <section className="tile">
        <div className="page-head">
          <div>
            <h1>Casinos</h1>
            <p>{a.rows.length} casinos, {a.label ?? win}. Every tracker's figure side by side, and how far apart they are. Open one for its profile.</p>
          </div>
        </div>
        <div className="toolbar">
          <label className="search">
            <Icon name="search" size={18} />
            <input type="search" placeholder="Search casinos" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search casinos" />
          </label>
          <div className="seg" role="group" aria-label="Filter">
            <button aria-pressed={only === 'all'} onClick={() => setOnly('all')}>All</button>
            <button aria-pressed={only === 'multi'} onClick={() => setOnly('multi')}>Cross-checked</button>
            <button aria-pressed={only === 'gap'} onClick={() => setOnly('gap')}>Widest gaps</button>
          </div>
        </div>
      </section>

      <section className="tile">
        <div className="scroll-x">
          <table className="data-table casinos-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Casino</th>
                <th className="num">Median</th>
                <th>Where the trackers land</th>
                <th className="num">Apart</th>
                {TRACKER_IDS.map((id) => <th key={id} className="num"><i className="swatch" style={{ background: `var(--tracker-${id})` }} aria-hidden /> {TRACKER_NAMES[id]}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 150).map((r, i) => <Row key={r.key} row={r} rank={i + 1} win={win} />)}
            </tbody>
          </table>
        </div>
        {!rows.length && <p className="empty">No casino matches that.</p>}
        {rows.length > 150 && <p className="empty">Showing the first 150 of {rows.length}. Search to narrow it.</p>}
      </section>
    </div>
  );
}

function Row({ row, rank, win }: { row: CasinoRow; rank: number; win: WindowKey }) {
  const logo = rank <= 40 && row.logos && Object.keys(row.logos).length ? logoUrl(row.key) : null;
  const href = `/casino?c=${encodeURIComponent(row.key)}&w=${win}`;
  const readings = TRACKER_IDS.flatMap((id) => {
    const v = row.by[id]?.value;
    return finite(v) ? [{ id, value: v, counted: row.consensusBy.includes(id) }] : [];
  });
  return (
    <tr className="row-link" onClick={openRowLink}>
      <td className="rank">{rank}</td>
      <td>
        <a className="casino" href={href}>
          <Logo src={logo} name={row.name} className="casino-blank" />
          {row.name}
        </a>
      </td>
      <td className="num"><b>{usd(row.consensus)}</b></td>
      <td className="rail-cell">{readings.length > 1 ? <Rail readings={readings} compact /> : <span className="muted">one source</span>}</td>
      <td className="num">
        {finite(row.spreadPct) ? <span className={`gap-pill g${gapStep(row.spreadPct)}`}>{pct(row.spreadPct, 0)}</span> : <span className="muted">—</span>}
      </td>
      {TRACKER_IDS.map((id) => {
        const v = row.by[id]?.value;
        return (
          <td key={id} className="num">
            {finite(v) ? usd(v) : <span className="muted">—</span>}
            {finite(v) && !row.consensusBy.includes(id) ? <span className="muted"> *</span> : null}
          </td>
        );
      })}
    </tr>
  );
}
